import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ButtonRendererComponent } from 'src/app/renderer/button-renderer.component';
import { DashboardService } from 'src/app/_services/dashboard.service';
import { LanguageTranslateService } from 'src/app/_services/language-translate.service';
import { UtilServiceService } from 'src/app/_services/utilService/util-service.service';
import { ErrorUtil } from 'src/app/_utilities/error';
import { SubscriptionUtil } from 'src/app/_utilities/subscription';
import { TableHeaderToolTipComponent } from 'src/app/shared/table-header-tool-tip/table-header-tool-tip.component';
import { AuthenticationService } from 'src/app/auth/authentication.service';
import moment from 'moment-timezone';
import {
  formatMaintenanceDateTime,
  formatMaintenanceDisplayDate,
  getMaintenanceDateRange,
} from '../../maintenance-cmms/maintenance-date-range.util';

@Component({
  selector: 'app-compliance-cert-inspec-list',
  templateUrl: './compliance-cert-inspec-list.component.html',
  styleUrl: './compliance-cert-inspec-list.component.scss',
  standalone: false,
})
export class ComplianceCertInspecListComponent
  extends SubscriptionUtil
  implements OnInit, OnDestroy
{
  public filterActive = true;
  title: any;
  data: any;

  totalRecord: any;
  searchValue: any;
  searchValueData = false;
  translate: any;

  gridApi: any;
  gridOptions: any;
  columnDefs: any;
  defaultColDef: any;
  overlayNoRowsTemplate: any;
  frameworkComponents: any;
  pageSize: any = 25;
  gridParams: any;
  startFrom: any;
  parameters: any;
  records: any[] = [];

  timeRangeOptions = [
    { label: 'Last Year', value: 'LAST_YEAR' },
    { label: 'Last Quarter', value: 'LAST_QUARTER' },
    { label: 'Last Month', value: 'LAST_MONTH' },
    { label: 'Last 7 Days', value: 'LAST_7_DAYS' },
    { label: 'Next 7 Days', value: 'NEXT_7_DAYS' },
    { label: 'Next Month', value: 'NEXT_MONTH' },
    { label: 'Next Quarter', value: 'NEXT_QUARTER' },
    { label: 'Next Year', value: 'NEXT_YEAR' },
    { label: 'Custom', value: 'CUSTOM' },
  ];
  equipmentTypeOptions: any[] = [];
  groupOptions: any[] = [];
  serviceZoneOptions: any[] = [];
  equipmentStatusOptions: any[] = [];

  filteredOptions = {
    dateFilter: 'LAST_7_DAYS',
    assetTypeIds: null as string | number | null,
    assetServiceZoneIds: null as string | number | null,
    jobOrderServiceZoneIds: null as string | number | null,
    userGroupIds: null as string | number | null,
    assetId: null as string | number | null,
    startDateTime: null as Date | null,
    endDateTime: null as Date | null,
  };
  customDateRange: Date[] | null = null;

  get recordType(): 'CERTIFICATION' | 'INSPECTION' {
    return this.data?.recordType === 'INSPECTION' ? 'INSPECTION' : 'CERTIFICATION';
  }

  get isCertification(): boolean {
    return this.recordType === 'CERTIFICATION';
  }

  constructor(
    public modalRef: BsModalRef,
    private dashboardService: DashboardService,
    private errorUtil: ErrorUtil,
    private languageTranslateService: LanguageTranslateService,
    private router: Router,
    private util: UtilServiceService,
    private auth: AuthenticationService
  ) {
    super();
    this.preInit();
  }

  ngOnInit(): void {
    this.init();
  }

  ngOnDestroy(): void {
    this.onDestroy();
  }

  /******************************************** Pre Init ******************************************/
  private preInit() {
    this.frameworkComponents = {
      buttonRenderer: ButtonRendererComponent,
    };

    this.gridOptions = {
      cacheBlockSize: 25,
      paginationPageSize: 25,
      rowModelType: 'infinite',
      sortingOrder: ['desc', 'asc'],
      onGridSizeChanged: (params: any) => {
        params.api.sizeColumnsToFit();
      },
      tooltipShowDelay: 100,
    };

    this.defaultColDef = {
      sortable: true,
      resizable: true,
      filter: false,
      enableRowGroup: true,
      enablePivot: true,
      enableValue: true,
      tooltipComponent: TableHeaderToolTipComponent,
    };

    this.overlayNoRowsTemplate =
      '<span style="padding: 10px; font-size: 14px;">Record not found</span>';
  }

  /******************************************** Init ******************************************/
  private init() {
    this.title =
      this.title ||
      (this.isCertification ? 'Certifications' : 'Inspections');
    this.initFilterData();
    this.getLanguageData();
  }

  private initFilterData(): void {
    const filters = this.data?.filters || {};
    this.equipmentTypeOptions = this.data?.equipmentTypeOptions || [];
    this.groupOptions = this.data?.groupOptions || [];
    this.serviceZoneOptions = this.data?.serviceZoneOptions || [];
    this.equipmentStatusOptions = this.data?.equipmentStatusOptions || [];

    this.filteredOptions = {
      dateFilter: filters.dateFilter || 'LAST_7_DAYS',
      assetTypeIds: filters.assetTypeIds ?? null,
      assetServiceZoneIds: filters.assetServiceZoneIds ?? null,
      jobOrderServiceZoneIds: filters.jobOrderServiceZoneIds ?? null,
      userGroupIds: filters.userGroupIds ?? null,
      assetId: filters.assetId ?? null,
      startDateTime: filters.startDateTime
        ? new Date(filters.startDateTime)
        : null,
      endDateTime: filters.endDateTime ? new Date(filters.endDateTime) : null,
    };

    if (
      this.filteredOptions.dateFilter === 'CUSTOM' &&
      this.filteredOptions.startDateTime &&
      this.filteredOptions.endDateTime
    ) {
      this.customDateRange = [
        this.filteredOptions.startDateTime,
        this.filteredOptions.endDateTime,
      ];
    } else {
      this.customDateRange = null;
      this.syncDateRangeFromFilter();
    }
  }

  private syncDateRangeFromFilter(): void {
    const range = getMaintenanceDateRange(
      this.filteredOptions.dateFilter,
      this.filteredOptions.startDateTime,
      this.filteredOptions.endDateTime
    );
    if (!range || this.filteredOptions.dateFilter === 'CUSTOM') {
      return;
    }
    this.filteredOptions.startDateTime = range.start;
    this.filteredOptions.endDateTime = range.end;
  }

  /******************************************** On Destroy ******************************************/
  private onDestroy() {
    super.ngOnDestroy();
  }

  /******************************************** Language ******************************************/
  getLanguageData() {
    this.push(
      this.languageTranslateService.wordSrc$.subscribe((data) => {
        if (Object.keys(data).length !== 0) {
          this.translate = data;
          this.initializeColumns();
        }
      })
    );
  }

  push(obs: any) {
    super.push(obs);
  }

  /******************************************** On Grid Ready ******************************************/
  public onGridReady(params: any) {
    this.gridParams = params;
    this.searchValue = this.searchValue ? this.searchValue : '';
    this.gridApi = params.api;

    const dataSource = {
      getRows: (params: any) => {
        this.startFrom = params.startRow + 1;

        if (params.sortModel.length <= 0) {
          this.parameters = { colId: 'dueDate', sort: 'desc' };
          params.sortModel.push(this.parameters);
        }

        const filters = this.filteredOptions;
        if (!filters?.dateFilter) {
          this.totalRecord = 0;
          params.successCallback([], 0);
          this.gridApi.showNoRowsOverlay();
          return;
        }

        if (
          filters.dateFilter === 'CUSTOM' &&
          (!filters.startDateTime || !filters.endDateTime)
        ) {
          this.totalRecord = 0;
          params.successCallback([], 0);
          this.gridApi.showNoRowsOverlay();
          return;
        }

        const apiParams = this.buildApiParams(filters, params);

        this.push(
          this.dashboardService.getComplianceRecords(apiParams).subscribe(
            (res: any) => {
              this.gridApi.hideOverlay();

              const list = this.extractList(res);
              this.records = list.map((item: any) => this.mapRow(item));
              const total = this.extractTotal(res, list);
              const body = res?.body ?? res;
              const status =
                body?.status ??
                res?.status ??
                (list.length ? 'OK' : 'NO_DATA');

              if (
                status === 'NO_DATA' ||
                status === 'SEARCH_NO_RESULTS' ||
                this.records.length === 0
              ) {
                this.gridApi.showNoRowsOverlay();
                this.totalRecord = 0;
                params.successCallback([], 0);
              } else {
                this.totalRecord = total;
                params.successCallback(this.records, total);
              }
            },
            (error) => {
              let errorTitle = error?.error?.title;
              if (error?.error?.type == 'dynamic-lang-error') {
                const data =
                  error.error.instance +
                  '.validationBE.' +
                  error.error.detail;
                errorTitle = this.languageTranslateService.getNestedValue(
                  this.translate,
                  data
                );
              }
              this.errorUtil.setErrorMessage(
                error.status,
                null,
                errorTitle,
                'error',
                2000
              );
              this.totalRecord = 0;
              params.successCallback([], 0);
              this.gridApi.showNoRowsOverlay();
            }
          )
        );
      },
    };

    this.gridApi.sizeColumnsToFit();
    this.gridApi.setGridOption('datasource', dataSource);
  }

  /******************************************** Build API Params ******************************************/
  private buildApiParams(filters: any, gridParams: any): any {
    const sortColId = gridParams.sortModel[0]?.colId || 'dueDate';
    const page = Math.floor(gridParams.startRow / this.pageSize) + 1;
    const assetTypeId = this.resolveFilterId(filters.assetTypeIds ?? filters.assetsTypeId);

    const apiParams: any = {
      recordType: this.recordType,
      dateFilter: filters.dateFilter,
      assetsTypeId: assetTypeId,
      assetTypeIds: assetTypeId,
      userGroupIds: this.resolveFilterId(filters.userGroupIds),
      assetServiceZoneIds: this.resolveFilterId(filters.assetServiceZoneIds),
      assetId: this.resolveFilterId(filters.assetId),
      columnName: this.mapSortColumn(sortColId),
      sortOrder: gridParams.sortModel[0]?.sort || 'desc',
      page,
      perPage: this.pageSize,
      search: this.searchValue || null,
    };

    if (filters.dateFilter === 'CUSTOM') {
      const range = getMaintenanceDateRange(
        filters.dateFilter,
        filters.startDateTime,
        filters.endDateTime
      );
      if (range) {
        apiParams.fromDate = formatMaintenanceDateTime(range.start);
        apiParams.toDate = formatMaintenanceDateTime(range.end, true);
      }
    }

    return apiParams;
  }

  private resolveFilterId(value: any): string | number | null {
    if (value == null || value === '') {
      return null;
    }
    if (typeof value === 'object') {
      return value.id ?? value.value ?? null;
    }
    return value;
  }

  private mapSortColumn(colId: string): string {
    const columnMap: Record<string, string> = {
      equipmentName: 'assetName',
      equipmentStatus: 'assetStatusName',
      typeName: 'typeName',
      runningNo: 'runningNo',
      overdue: 'dueDate',
      dueDate: 'dueDate',
    };
    return columnMap[colId] || 'dueDate';
  }

  private extractList(res: any): any[] {
    const body = res?.body ?? res;
    if (Array.isArray(body)) {
      return body;
    }
    return body?.data ?? body?.content ?? body?.records ?? [];
  }

  private extractTotal(res: any, list: any[]): number {
    const body = res?.body ?? res;
    return (
      body?.total ??
      body?.totalElements ??
      res?.total ??
      res?.totalElements ??
      list.length
    );
  }

  private mapRow(item: any): any {
    const status = this.getAssetStatusDisplay(item?.assetStatusName);

    return {
      ...item,
      assetId: item?.assetId ?? null,
      equipmentId: item?.equipmentId || item?.assetId || null,
      recordId: item?.recordId,
      equipmentName: item?.assetName || '-',
      equipmentSerial: item?.assetSerialNo || '-',
      equipmentStatus: status.name,
      equipmentStatusClass: status.colorCode,
      typeName: item?.typeName || '-',
      runningNo: item?.runningNo || '-',
      assetRunningNo:
        item?.assetRunningNo ||
        item?.asset?.runningNo ||
        item?.equipmentRunningNo ||
        null,
      recordStatus: String(item?.status || '').toUpperCase(),
      dueDate: item?.dueDate || null,
      dueDateDisplay: this.formatDueDate(item?.dueDate),
      overdue: this.getOverdueLabel(item),
    };
  }

  private formatDueDate(dueDate: any): string {
    if (dueDate == null || dueDate === '') {
      return '-';
    }

    const dateValue =
      typeof dueDate === 'object'
        ? dueDate.date || dueDate.dateTime || dueDate.value || dueDate
        : dueDate;

    const timezone = this.auth.getUserTimezone();
    const dateFormat = localStorage.getItem('date_format')?.toUpperCase() || 'DD/MM/YYYY';
    const parsed = moment.tz(dateValue, timezone);

    if (!parsed.isValid()) {
      return '-';
    }

    return `${parsed.format(dateFormat)}`;
  }

  private getAssetStatusDisplay(
    assetStatusName: string | null | undefined
  ): { name: string; colorCode: string } {
    const name = assetStatusName || '-';
    const options = this.equipmentStatusOptions || [];
    const matched = options.find(
      (status: any) =>
        String(status?.name || status?.label || '').toLowerCase() ===
        String(name).toLowerCase()
    );

    return {
      name,
      colorCode: matched?.colorCode || '',
    };
  }

  private getOverdueLabel(item: any): string {
    const recordStatus = String(item?.status || '').toUpperCase();
    const dueDate = item?.dueDate;

    if (!dueDate) {
      return '-';
    }
    const endDate = new Date(dueDate);
    if (Number.isNaN(endDate.getTime())) {
      return '-';
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    const diffDays = Math.round((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const days = Math.abs(diffDays);

    if (recordStatus === 'OVERDUE' && diffDays < 0) {
      return `${days} day${days === 1 ? '' : 's'} overdue`;
    }

    if (diffDays === 0) {
      return 'Due today';
    }
    if(recordStatus === 'COMPLETED'){
      return 'Completed';
    }

    return `Overdue in ${diffDays} day${diffDays === 1 ? '' : 's'}`;
  }

  /******************************************** Search ******************************************/
  public onSearch() {
    if (this.searchValue && this.searchValue.length > 0) {
      this.searchValueData = true;
      this.refresh();
      return;
    }

   
  }
public onSearchCheck() {
    if (this.searchValue?.length == 0) {
      this.searchValueData = false;
      this.searchValue = '';
      this.refresh();
    }
  }

  public onClear() {
    this.searchValueData = false;
    this.searchValue = '';
    this.refresh();
  }

  /******************************************** Acts as Refresh ******************************************/
  public refresh() {
    this.onGridReady(this.gridParams);
  }

  /******************************************** View ******************************************/
  public view(e: any) {
    if (e.colDef.field === 'runningNo' && e.data?.recordId) {
      this.openComplianceRecord(e.data);
      return;
    }

    if (
      e.colDef.field === 'equipmentName' &&
      (e.data?.equipmentId || e.data?.assetId)
    ) {
      this.openAsset(e.data.equipmentId || e.data.assetId);
    }
  }

  private openAsset(assetId: string | number) {
    localStorage.setItem('activeIndexAssetView', '0');
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/dashboard/assets/view', assetId], {
        queryParams: { id: assetId },
      })
    );
    this.util.openItemToNewTab(url);
  }

  private openComplianceRecord(row: any): void {
    if (!row?.recordId) {
      return;
    }

    const path = this.isCertification
      ? ['/dashboard/assets/certification', row.recordId]
      : ['/dashboard/assets/inspection', row.recordId];
    const queryParams: { id: string | number; assetRunningNo?: string } = {
      id: row.recordId,
    };
    if (row.assetRunningNo) {
      queryParams.assetRunningNo = row.assetRunningNo;
    }

    const url = this.router.serializeUrl(
      this.router.createUrlTree(path, { queryParams })
    );
    this.util.openItemToNewTab(url);
  }

  /******************************************** Context Menu ******************************************/
  public getContextMenuItems = (params: any) => {
    params.node.data.field = params.column.colId;
    const results: any[] = [];

    if (params.column.colId === 'runningNo' && params.node.data?.recordId) {
      results.push({
        name: 'Open link in new tab',
        action: () => this.openComplianceRecord(params.node.data),
      });
    }

    if (
      params.column.colId === 'equipmentName' &&
      (params.node.data?.equipmentId || params.node.data?.assetId)
    ) {
      results.push({
        name: 'Open link in new tab',
        action: () =>
          this.openAsset(
            params.node.data.equipmentId || params.node.data.assetId
          ),
      });
    }

    return results;
  };

  /******************************************** toggle filter ******************************************/
  public toggleFilter() {
    this.filterActive = !this.filterActive;
  }

  get displayDateRange(): string {
    const range = getMaintenanceDateRange(
      this.filteredOptions.dateFilter,
      this.filteredOptions.startDateTime,
      this.filteredOptions.endDateTime
    );

    if (!range) {
      return '';
    }

    return `${formatMaintenanceDisplayDate(range.start)} - ${formatMaintenanceDisplayDate(range.end)}`;
  }

  get isCustomDateRangeRequired(): boolean {
    return (
      this.filteredOptions.dateFilter === 'CUSTOM' &&
      (!this.filteredOptions.startDateTime || !this.filteredOptions.endDateTime)
    );
  }

  onTimeRangeChange(value: string): void {
    this.filteredOptions.dateFilter = value;
    if (value !== 'CUSTOM') {
      this.customDateRange = null;
      this.syncDateRangeFromFilter();
      this.refresh();
    } else {
      this.filteredOptions.startDateTime = null;
      this.filteredOptions.endDateTime = null;
    }
  }

  onCustomDateRangeChange(range: Date[] | null): void {
    this.customDateRange = range;
    if (range?.[0] && range?.[1]) {
      this.filteredOptions.startDateTime = range[0];
      this.filteredOptions.endDateTime = range[1];
      this.refresh();
    } else if (range?.[0]) {
      this.filteredOptions.startDateTime = range[0];
      this.filteredOptions.endDateTime = null;
    } else {
      this.filteredOptions.startDateTime = null;
      this.filteredOptions.endDateTime = null;
    }
  }

  onFilterChange(
    key: 'assetTypeIds' | 'userGroupIds' | 'assetServiceZoneIds',
    value: any
  ): void {
    this.filteredOptions[key] = value;
    this.refresh();
  }

  clearAll(): void {
    this.filteredOptions = {
      dateFilter: 'LAST_7_DAYS',
      assetTypeIds: null,
      assetServiceZoneIds: null,
      jobOrderServiceZoneIds: null,
      userGroupIds: null,
      assetId: null,
      startDateTime: null,
      endDateTime: null,
    };
    this.customDateRange = null;
    this.syncDateRangeFromFilter();
    this.refresh();
  }

  /******************************************** Columns ******************************************/
  private initializeColumns() {
    this.overlayNoRowsTemplate =
      '<span style="padding: 10px; font-size: 14px;">' +
      (this.translate?.general?.messages?.noRecordsFound || 'Record not found') +
      '</span>';

    const typeHeader = this.isCertification
      ? 'Certification Type'
      : 'Inspection Name';
    const numberHeader = this.isCertification
      ? 'Certification Number'
      : 'Inspection Number';

    this.columnDefs = [
      {
        headerName: this.translate?.asset.label.singular,
        field: 'equipmentName',
        width: 180,
        headerTooltip: '#',
        cellClass: (params: any) => {
          if (params?.data?.equipmentId || params?.data?.assetId) {
            return ['text-1-5 font-weight-bold table_default_color'];
          }
          return [];
        },
      },
      {
        headerName: this.translate?.asset.label.singular + ' ' + this.translate?.asset.fields.status.singular,
        field: 'equipmentStatus',
        width: 150,
        headerTooltip: '#',
        cellRenderer: function (params: any) {
          if (!params?.data) {
            return '';
          }
          const status = params.data.equipmentStatus || '-';
          const color = params.data.equipmentStatusClass;
          if (color) {
            return (
              '<span class="p-mr-2 p-tag p-component" style="background-color:' +
              color +
              '50 !important ;color:' +
              color +
              '"><span class="p-tag-value text-dark">' +
              status +
              '</span></span>'
            );
          }
          return status;
        },
      },
      {
        headerName: typeHeader,
        field: 'typeName',
        width: 180,
        headerTooltip: '#',
      },
      {
        headerName: numberHeader,
        field: 'runningNo',
        width: 160,
        headerTooltip: '#',
        cellClass: function () {
          return ['text-1-5 font-weight-bold table_default_color'];
        },
      },
      {
        headerName: 'Due Date',
        field: 'dueDateDisplay',
        colId: 'dueDate',
        width: 170,
        headerTooltip: '#',
      },
      {
        headerName: this.translate?.asset.fields.status.singular,
        field: 'overdue',
        width: 150,
        headerTooltip: '#',
        cellRenderer: function (params: any) {
          if (!params?.data) {
            return '';
          }

          if (!params.data.dueDate) {
            return '<span class="maintenance-jobs__cell-value">-</span>';
          }

          const label = params.data.overdue || '-';
          if (params.data.recordStatus === 'OVERDUE') {
            return (
              '<span class="maintenance-jobs__cell-value maintenance-jobs__cell-value--overdue">' +
              label +
              '</span>'
            );
          }

          return (
            '<span class="maintenance-jobs__cell-value">' + label + '</span>'
          );
        },
      },
    ];

    if (this.gridApi) {
      this.gridApi.setGridOption('columnDefs', this.columnDefs);
      setTimeout(() => {
        this.gridApi.sizeColumnsToFit();
      }, 300);
    }
  }
}
